import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/PageHeader';
import api from 'services/api';

const API_BASE_URL = process.env.REACT_APP_API_URL || (
  process.env.NODE_ENV === 'production' 
    ? '/api' 
    : 'http://localhost:5000/api'
);

interface BugReport {
  id: number;
  user_id: number | null;
  username: string | null;
  page: string;
  description: string;
  created_at: string;
}

const BugReportsPage: React.FC = () => {
  const [bugReports, setBugReports] = useState<BugReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [selectedReport, setSelectedReport] = useState<BugReport | null>(null);
  const history = useHistory();
  const { logout } = useAuth();

  useEffect(() => {
    fetchBugReports();
  }, []);

  const fetchBugReports = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`${API_BASE_URL}/bug-reports`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setBugReports(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Fetch error:', err);
      const error = err;
      setError((error && error.response && error.response.data && error.response.data.message) || 'Failed to fetch bug reports');
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this bug report?')) {
      return;
    }

    setDeletingId(id);
    try {
      const token = localStorage.getItem('token');
      console.log('Deleting bug report:', id);
      console.log('Token:', token);
      
      const response = await api.delete(`${API_BASE_URL}/bug-reports/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('Delete response:', response.data);
      
      // Remove from state
      setBugReports(bugReports.filter(report => report.id !== id));
    } catch (err) {
      console.error('Delete error:', err.response || err);
      const error = err;
      alert((error && error.response && error.response.data && error.response.data.message) || 'Failed to delete bug report');
    } finally {
      setDeletingId(null);
    }
  };

  const handleLogout = () => {
    logout();
    history.push('/');
  };

  const handleBackToDashboard = () => {
    history.push('/dashboard');
  };

  // Custom buttons for Bug Reports page
  const customButtons = [
    <button key="dashboard" onClick={handleBackToDashboard} className="btn-secondary">
      Back to Dashboard
    </button>,
    <button key="logout" onClick={handleLogout} className="btn-primary">
      Logout
    </button>
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-xl">Loading bug reports...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-xl text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Navigation Header */}
      <PageHeader additionalButtons={customButtons} />

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">All Bug Reports</h1>
        
        {bugReports.length === 0 ? (
          <div className="text-center text-gray-500 mt-8">
            No bug reports submitted yet.
          </div>
        ) : (
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Page
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {bugReports.map((report) => (
                  <tr key={report.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedReport(report)}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      #{report.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {report.username || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {report.page}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="max-w-md truncate" title={report.description}>
                        {report.description}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(report.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => handleDelete(report.id)}
                        disabled={deletingId === report.id}
                        className="text-red-600 hover:text-red-900 disabled:opacity-50"
                      >
                        {deletingId === report.id ? 'Deleting...' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bug Report Detail Modal */}
      {selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedReport(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-bold text-gray-900">Bug Report #{selectedReport.id}</h2>
                <button
                  onClick={() => setSelectedReport(null)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">User</label>
                  <p className="text-gray-900">{selectedReport.username || 'Unknown'}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Page</label>
                  <p className="text-gray-900">{selectedReport.page}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Description</label>
                  <div className="bg-gray-50 p-4 rounded-md text-gray-900 whitespace-pre-wrap">
                    {selectedReport.description}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Submitted At</label>
                  <p className="text-gray-900">{formatDate(selectedReport.created_at)}</p>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setSelectedReport(null)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    handleDelete(selectedReport.id);
                    setSelectedReport(null);
                  }}
                  disabled={deletingId === selectedReport.id}
                  className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:opacity-50"
                >
                  {deletingId === selectedReport.id ? 'Deleting...' : 'Delete Report'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BugReportsPage;
